import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import type { CSSProp } from 'styled-components';
import { StyledParallaxImage } from './styled';

interface ParallaxImageProps {
    src: string;
    alt: string;
    speed?: number;
    smSpeed?: number;
    customStyles?: CSSProp;
    width?: number | string;
    height?: number | string;
}

const ParallaxImage: React.FC<ParallaxImageProps> = ({
    src,
    alt,
    speed = 0.5,
    smSpeed,
    customStyles,
    width,
    height,
}) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const theme = useTheme();
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!isLoaded || !imageRef.current) return;

        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    if (imageRef.current) {
                        const rect = imageRef.current.getBoundingClientRect();

                        const viewportCenter = window.innerHeight / 2;
                        const elementCenter = rect.top + rect.height / 2;

                        const distanceFromCenter = viewportCenter - elementCenter;

                        const isSmallScreen = window.innerWidth <= theme.breakpoints.values.sm;
                        const currentSpeed = isSmallScreen ? smSpeed ?? speed : speed;

                        const offsetY = distanceFromCenter * currentSpeed * -1;

                        imageRef.current.style.translate = `0 ${offsetY}px`;
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isLoaded, speed, smSpeed, theme.breakpoints.values.sm]);

    return (
        <StyledParallaxImage
            ref={imageRef}
            src={src}
            alt={alt}
            width={width}
            height={height}
            customStyles={customStyles}
            onLoad={() => setIsLoaded(true)}
        />
    );
};

export default ParallaxImage;
